import { useState, useEffect } from 'react';
import {
  NON_ENERGY_DEFAULTS_FALLBACK,
  type NonEnergyDefaultsConfig,
} from '../lib/nonEnergyDefaultsRegistry';
import { sysConfigApiService } from '../services/sysConfigApiService';

// Module-level cache so every component shares the same fetch — mirrors usePowerEnvDefaults.
let _cached: NonEnergyDefaultsConfig | null = null;
let _promise: Promise<NonEnergyDefaultsConfig> | null = null;

async function fetchDefaults(): Promise<NonEnergyDefaultsConfig> {
  if (_cached) return _cached;
  if (_promise) return _promise;

  _promise = sysConfigApiService.get('non_energy_defaults').then(result => {
    const raw = (result.success ? result.data?.value : undefined) as Partial<NonEnergyDefaultsConfig> | undefined;
    _cached = {
      meterAgreement: typeof raw?.meterAgreement === 'number'
        ? raw.meterAgreement
        : NON_ENERGY_DEFAULTS_FALLBACK.meterAgreement,
      retailers: (raw?.retailers && typeof raw.retailers === 'object')
        ? raw.retailers
        : NON_ENERGY_DEFAULTS_FALLBACK.retailers,
    };
    _promise = null;
    return _cached;
  }).catch(() => {
    _promise = null;
    return { ...NON_ENERGY_DEFAULTS_FALLBACK };
  });

  return _promise;
}

/**
 * Returns the non-energy charge defaults from sys_config.non_energy_defaults:
 * the static Meter Agreement fee plus each retailer's Supplementary Metering /
 * Other Service Charge.
 *
 * Falls back to NON_ENERGY_DEFAULTS_FALLBACK until loaded (or if the row is missing).
 * Resolve a specific retailer's values with resolveNonEnergyDefaults() from
 * nonEnergyDefaultsRegistry.ts — it handles the varying `retailer.name` spellings.
 */
export const useNonEnergyDefaults = (): NonEnergyDefaultsConfig => {
  const [defaults, setDefaults] = useState<NonEnergyDefaultsConfig>(_cached ?? NON_ENERGY_DEFAULTS_FALLBACK);

  useEffect(() => {
    if (_cached) return; // Already loaded — state is already correct
    fetchDefaults().then(setDefaults);
  }, []);

  return defaults;
};
